// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/empty-states.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import {
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Empty as EmptyV1,
} from "@repo/design-system/components/ui/empty";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps, ReactNode } from "react";
import { Button } from "./button";

/**
 * v2 Empty State — restyles v1's `Empty` (`components/ui/empty.tsx`) to
 * match the Figma "Remote Leverage — Design System" file, "Empty state"
 * component node `693:6397` (shown inside the "Components Section" docs
 * node `705:35`). Structurally this is the closest thing to v2 `Task`:
 * v1's own layout primitives (`EmptyMedia`/`EmptyHeader`/`EmptyTitle`/
 * `EmptyDescription`/`EmptyContent`) are reused unstyled for
 * positioning/semantics exactly as `empty.tsx` defines them, and this file
 * only overrides the visual layer (colors, radius, sizing, spacing) plus
 * assembles them into one fixed illustration+heading+description+actions
 * shape driven by props — the same reason `Task` doesn't re-export
 * `item.tsx`'s sub-parts (they're consumed internally to build one fixed
 * composite, not exposed loose the way `card.tsx` re-exports parts it
 * never touches itself).
 *
 * The two action buttons compose v2 `Button` (`./button.tsx`) directly —
 * `primaryAction`/`secondaryAction` are typed as v2 `Button` props plus a
 * `label`, so a consumer can never render a raw v1 button here even by
 * accident. `secondaryAction` renders with v2 Button's `secondary` variant
 * and `primaryAction` with its `default` (primary) variant; both already
 * match this Figma component's CTA colors pixel-for-pixel (v2 Button's
 * primary bg `#8a2be2` / secondary bg `#ffffff` + fg `#111827`), so no
 * additional per-button override classes are needed here.
 *
 * The illustration is Figma's bespoke gradient-orb "Profile" graphic, not
 * a generic icon — per the v2 Empty States ticket, that's deliberately
 * NOT reimplemented as a bespoke asset here. `icon` is a plain `ReactNode`
 * slot the consumer fills (a lucide icon, a brand illustration, etc.).
 * `mediaVariant` passes straight through to v1 `EmptyMedia`'s existing
 * `default`/`icon` variant map (`icon` = the muted rounded chip treatment
 * v1 already ships) — `default` is this component's default, matching the
 * Figma reference where the illustration has no background chip.
 *
 * Deliberately out of scope:
 * - Figma's literal `px-[200px]` container padding is an artifact of the
 *   792px-wide docs showcase frame the component happens to be pinned
 *   inside, not a real design token (there's no 200 step in the spacing
 *   scale, and it would overflow any container narrower than ~600px in
 *   real usage). The 40px vertical padding (`py-[40px]`) IS a real
 *   spacing-scale value, reused as `p-10`; horizontal padding otherwise
 *   follows v1 `Empty`'s own existing responsive pattern (smaller on
 *   mobile) rather than a fixed pixel figure.
 * - `EmptyHeader`'s v1 `max-w-sm` and `EmptyContent`'s v1 `max-w-sm` are
 *   overridden to `max-w-none` — Figma's sample copy is `whitespace-nowrap`
 *   (not wrapped to a fixed column width), so constraining either block
 *   would diverge from the reference for any real (longer) copy.
 * - No default/fallback illustration is rendered when `icon` is omitted —
 *   inventing a placeholder illustration is exactly what this ticket says
 *   not to do; an icon-less empty state (heading + description only) is a
 *   legitimate, supported shape of this component.
 */

type EmptyStateAction = Omit<
  ComponentProps<typeof Button>,
  "asChild" | "children"
> & {
  label: ReactNode;
};

type EmptyStateProps = Omit<
  ComponentProps<typeof EmptyV1>,
  "title" | "children"
> & {
  icon?: ReactNode;
  mediaVariant?: ComponentProps<typeof EmptyMedia>["variant"];
  title: ReactNode;
  description?: ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
};

function renderAction(
  action: EmptyStateAction | undefined,
  defaultVariant: ComponentProps<typeof Button>["variant"]
) {
  if (!action) {
    return null;
  }
  const { label, variant = defaultVariant, ...buttonProps } = action;
  return (
    <Button variant={variant} {...buttonProps}>
      {label}
    </Button>
  );
}

function EmptyState({
  className,
  icon,
  mediaVariant = "default",
  title,
  description,
  primaryAction,
  secondaryAction,
  ...props
}: EmptyStateProps) {
  return (
    <EmptyV1
      className={cn(
        "ds-v2 gap-6 rounded-[20px] border border-(--v2-empty-state-border) border-solid bg-(--v2-empty-state-bg) p-6 md:p-10",
        className
      )}
      {...props}
    >
      {icon ? (
        <EmptyMedia
          className={cn(
            "mb-0",
            mediaVariant === "default"
              ? "size-[100px] bg-transparent [&_svg:not([class*='size-'])]:size-full"
              : null
          )}
          variant={mediaVariant}
        >
          {icon}
        </EmptyMedia>
      ) : null}
      <EmptyHeader className="max-w-none gap-2">
        <EmptyTitle className="text-(--v2-empty-state-title-fg) text-[26px] leading-[1.32] tracking-normal">
          {title}
        </EmptyTitle>
        {description ? (
          <EmptyDescription className="max-w-none text-(--v2-empty-state-description-fg) text-[16px] leading-[1.48]">
            {description}
          </EmptyDescription>
        ) : null}
      </EmptyHeader>
      {primaryAction || secondaryAction ? (
        <EmptyContent className="w-auto max-w-none flex-row items-center justify-center gap-3">
          {renderAction(secondaryAction, "secondary")}
          {renderAction(primaryAction, "default")}
        </EmptyContent>
      ) : null}
    </EmptyV1>
  );
}

export { EmptyState };
export type { EmptyStateAction, EmptyStateProps };
